/**
 * Settings → Access control — the admin-editable IP allowlist gating
 * the public surface (v0.30.0 hardening).
 *
 * Semantics (mirrors services/access_control.py):
 * - Non-empty range list → /api/ext/* (the API-key channel) only
 *   accepts calls from those ranges.
 * - "Enforce for the whole site" extends the allowlist to every page
 *   (login included).
 * - LAN/private + loopback sources are ALWAYS allowed, so you can't
 *   brick LAN access; the backend also refuses a config that would
 *   lock out the admin submitting it.
 * - Empty list = allow all.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

import { api, asApiError } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { useToast } from "../../components/Toast";

type AccessConfig = {
  allowed_api_ranges: string[];
  enforce_for_ui: boolean;
  updated_by: string | null;
  updated_at: string | null;
};

export function AccessControl() {
  const qc = useQueryClient();
  const toast = useToast();

  const cfgQ = useQuery<AccessConfig>({
    queryKey: ["v2", "access-config"],
    queryFn: async () =>
      (await api.get<AccessConfig>("/settings/access")).data,
    retry: false,
  });

  const [ranges, setRanges] = useState("");
  const [enforceUi, setEnforceUi] = useState(false);

  useEffect(() => {
    const c = cfgQ.data;
    if (!c) return;
    setRanges((c.allowed_api_ranges ?? []).join("\n"));
    setEnforceUi(c.enforce_for_ui);
  }, [cfgQ.data]);

  const save = useMutation({
    mutationFn: async () => {
      const list = ranges
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const body = {
        allowed_api_ranges: list,
        enforce_for_ui: enforceUi,
      };
      return (await api.put<AccessConfig>("/settings/access", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "access-config"] });
      toast.push("Access control saved", "success");
    },
    onError: (err) =>
      toast.push(asApiError(err).detail || "Save failed", "error"),
  });

  const rangeCount = ranges
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean).length;

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <header className="flex items-center gap-2">
        <h1 className="inline-flex items-center gap-2 text-lg font-semibold text-slate-100">
          <ShieldCheck className="h-4 w-4" />
          Access control (IP allowlist)
        </h1>
      </header>

      <div className="rounded border border-slate-800 bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-400">
        With one or more ranges configured, the external API
        (<code className="font-mono">/api/ext/*</code>) only accepts calls
        from those addresses. Loopback and private/LAN ranges
        (10/8, 172.16/12, 192.168/16) are <strong>always allowed</strong> so
        local access can never be locked out. An empty list allows all
        sources.
      </div>

      <label className="flex flex-col gap-1 text-xs text-slate-300">
        Allowed ranges (CIDR or bare IP, one per line)
        <textarea
          value={ranges}
          onChange={(e) => setRanges(e.target.value)}
          rows={8}
          placeholder={"203.0.113.0/24\n198.51.100.7"}
          spellCheck={false}
          className="rounded border border-slate-700 bg-slate-950 px-2 py-1.5 font-mono text-xs text-slate-100 placeholder:text-slate-600 focus-visible:border-emerald-700 focus-visible:outline-none"
        />
      </label>

      <label className="inline-flex items-start gap-2 rounded border border-red-900/60 bg-red-950/20 p-3 text-xs text-slate-200">
        <input
          type="checkbox"
          checked={enforceUi}
          onChange={(e) => setEnforceUi(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 accent-red-600"
        />
        <span>
          <strong>Enforce for the whole site</strong> — extend the allowlist
          to every page including login. Public visitors outside the ranges
          are blocked entirely. Private/LAN sources always remain allowed,
          and the server refuses a save that would lock out your current
          address.
        </span>
      </label>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-xs text-emerald-100 hover:bg-emerald-900/70 disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <span className="text-[11px] text-slate-500">
          {rangeCount === 0
            ? "no ranges — allow all"
            : `${rangeCount} range${rangeCount === 1 ? "" : "s"}`}
        </span>
        {cfgQ.data?.updated_at && (
          <span className="text-[10px] text-slate-500">
            last saved by {cfgQ.data.updated_by ?? "—"} ·{" "}
            <LocalTime iso={cfgQ.data.updated_at} />
          </span>
        )}
      </div>
      <p className="text-[10px] text-slate-600">
        Saving is admin-only (users in{" "}
        <code className="font-mono">CHASSIS_API_KEY_ADMINS</code>); reading is
        open to any operator. Recovery if you ever do get locked out: connect
        from the LAN, or{" "}
        <code className="font-mono">
          UPDATE access_config SET enforce_for_ui = false WHERE id = 1;
        </code>
      </p>
    </section>
  );
}
